/* eslint class-methods-use-this: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import SessionStore from 'stores/SessionStore';
import {
  getCurrentDeliveryOrder,
  getDocKey,
  openSignedUrlInNewTab,
} from 'utils/documents';
import { sluggify } from 'utils/localization/utils';
import LoadingCircle from 'components/ui/LoadingCircle';
import { getDocumentsV2, getSignedUrl } from 'utils/DocumentsAPIUtils';
import {
  carrierViewableDocTypesMap,
  shipperViewableDocTypesMap,
} from 'config/documents';
import AddButton from 'components/v2/buttons/AddButton';
import DocumentIcon from 'components/v2/icons/DocumentIcon';
import { showToast } from 'stores/ToastStore';
import { DocumentUploadModal } from './DocumentUploadModal';

const openSignedUrl = (key) => async (event) => {
  event.preventDefault();
  try {
    const { data: signedUrl } = await getSignedUrl(key);

    openSignedUrlInNewTab(signedUrl?.getSignedUrl?.signedUrl);
  } catch (e) {
    console.error('ShipmentListDocuments: error getting signed url: ', e);
  }
};

class ShipmentListDocuments extends React.Component {
  static propTypes = {
    shipment: PropTypes.object,
  };
  static defaultProps = {
    shipment: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      showUploadModal: false,
      userRole: null,
      documents: [],
      isLoading: true,
    };
  }

  componentDidMount() {
    const user = SessionStore.getUser();
    this.setState({ userRole: user._type });

    if (this.props.shipment?._id) {
      this.initDocuments();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.shipment?._id !== prevProps.shipment?._id) {
      this.initDocuments();
    }
  }

  showGetDocumentsErrorToast = (err) => {
    const { t } = this.props;
    showToast(
      t('GET_DOCUMENT_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }),
      'error',
    );
  };

  async initDocuments() {
    this.setState({ isLoading: true });

    const { data, errors } = await getDocumentsV2([this.props.shipment._id]);

    if (errors) {
      this.showGetDocumentsErrorToast(errors[0].message);
      return;
    }

    const documents = data?.getDocumentsV2?.documents;

    this.setState({ documents, isLoading: false });
  }

  renderDocuments() {
    const { t } = this.props;

    if (this.state.isLoading) {
      return <LoadingCircle text={t('LOADING')} />;
    }

    if (!this.state.documents.length) {
      return <span>{t('NO_DOCS_ATTACHED')}</span>;
    }

    const { userRole } = this.state;
    const scopedDocs = this.state.documents.filter((doc) => {
      if (userRole === 'Admin') {
        return true;
      }
      const docType = getDocKey(doc.type);

      if (userRole === 'Shipper') {
        return !!shipperViewableDocTypesMap[docType];
      }

      return !!carrierViewableDocTypesMap[docType];
    });

    return this.keepLatestDeliveryOrder(scopedDocs).map((doc) => {
      const tooltip = <Tooltip id={doc._id}>{t(sluggify(doc.name))}</Tooltip>;
      return (
        <div className="px-1" key={doc._id}>
          <a href={doc.key} onClick={openSignedUrl(doc.key)}>
            <OverlayTrigger
              placement="top"
              overlay={tooltip}
              style={{ display: 'inline-block' }}
            >
              <div>
                <DocumentIcon className="w-4 h-4 mb-1" />
              </div>
            </OverlayTrigger>
          </a>
        </div>
      );
    });
  }

  keepLatestDeliveryOrder = (docs) => {
    const deliveryOrder = getCurrentDeliveryOrder(docs);
    if (deliveryOrder) {
      return docs
        .filter((doc) => doc.key !== 'delivery_order')
        .concat([deliveryOrder]);
    }
    return docs;
  };

  renderUploadButton() {
    const { t } = this.props;
    const id = `upload-document-${this.props.shipment._id}`;
    const addDocumentTooltip = <Tooltip id={id}>{t('ADD_DOCUMENT')}</Tooltip>;
    return (
      <OverlayTrigger
        placement="top"
        overlay={addDocumentTooltip}
        style={{ display: 'inline-block' }}
      >
        <AddButton size="small" onClick={() => this.showUploadModal()} />
      </OverlayTrigger>
    );
  }

  showUploadModal = () => {
    this.setState({ showUploadModal: true });
  };

  hideUploadModal = () => {
    this.setState({ showUploadModal: false });
  };

  onUpload = () => {
    this.initDocuments();
  };

  render() {
    const { t } = this.props;
    return (
      <div>
        <div className="flex flex-wrap items-center -mx-1">
          {this.renderDocuments()}
        </div>
        {this.renderUploadButton()}
        {this.state.showUploadModal && (
          <DocumentUploadModal
            show={this.state.showUploadModal}
            onHide={this.hideUploadModal}
            onUpload={this.onUpload}
            user={this.state.userRole}
            shipment={this.props.shipment}
            t={t}
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(ShipmentListDocuments);
